import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
  AfterViewInit,
  NgZone
} from '@angular/core';
import { SurveyService } from '../../../services/surveys.service';
import { ModalAddAttachmentComponent } from '../../../modals/modal-add-attachment/modal-add-attachment.component';
import { MatDialog } from '@angular/material/dialog';
import { ModalShowEvidencesComponent } from '../../../modals/modal-show-evidences/modal-show-evidences.component';
import { UserService } from '../../../services/user.service';
import { UnitsService } from '../../../services/config/units.service';
import { environment } from '../../../../environments/environment';
import { faCommentDots, faFolderOpen } from '@fortawesome/free-solid-svg-icons';

declare var google: any;


@Component({
    selector: 'app-single-question',
    templateUrl: './single-question.component.html',
    styleUrls: ['./single-question.component.scss'],
    standalone: false
})
export class SingleQuestionComponent implements OnInit, AfterViewInit {
  @Input() question: any;
  @Input() isDeletable: boolean = false;
  @Input() isResponse: boolean = false;
  @Input() editable: boolean = false;
  @Input() survey: any;
  @Input() accordionOpened: boolean = true;
  @Input() clonable: boolean = true;

  @Output() questionDuplicated = new EventEmitter<any>();
  @Output() questionDeleted = new EventEmitter<number>();

  @ViewChild('openAnswerInput') openAnswerInput: ElementRef;

  faCommentDots = faCommentDots;
  faFolderOpen = faFolderOpen;

  private choiceIdCounter = 1;
  private isValidGoogleMapSelection: boolean = true;

  constructor(
    public surveyService: SurveyService,
    public dialog: MatDialog,
    public unitsService: UnitsService,
    public userService: UserService,
    private el: ElementRef,
    private renderer: Renderer2,
    private ngZone: NgZone
  ) { }

  public ngOnInit(): void {
    this.initializeQuestion();

    // Blocca gli input se la survey è già risposta
    if (this.survey?.answered && this.isResponse) {
      setTimeout(() => {
        this.disableAllInputs();
      }, 0);
    }
  }

  public ngAfterViewInit(): void {
    if (this.isResponse && this.isOpenedQuestion && this.getCustoms.use_google_maps && this.userService.currentOrganizationHasFeature('google_maps_integration')) {
      this.loadGoogleMapsScript();
    }
  }

  loadGoogleMapsScript() {
    if ((window as any).google && (window as any).google.maps) {
      this.initAutocomplete();
      return;
    }

    const scriptId = 'google-maps-script';
    if (document.getElementById(scriptId)) {
      const existingScript = document.getElementById(scriptId) as HTMLScriptElement;
      const originalOnLoad = existingScript.onload;
      existingScript.onload = (e) => {
        if (originalOnLoad) (originalOnLoad as any)(e);
        this.initAutocomplete();
      };
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapsApiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.initAutocomplete();
    };
    document.body.appendChild(script);
  }

  initAutocomplete() {
    if (!this.openAnswerInput) return;

    // assume valid initially if there is an answer (e.g. loaded from backend)
    this.isValidGoogleMapSelection = !!this.question.response.answer;

    this.renderer.listen(this.openAnswerInput.nativeElement, 'input', () => {
      this.isValidGoogleMapSelection = false;
    });

    this.renderer.listen(this.openAnswerInput.nativeElement, 'blur', () => {
      setTimeout(() => {
        if (!this.isValidGoogleMapSelection) {
          this.ngZone.run(() => {
            this.question.response.answer = '';
            if (this.question.response.customs) {
              this.question.response.customs.place = null;
            }
          });
        }
      }, 200);
    });

    const autocomplete = new google.maps.places.Autocomplete(this.openAnswerInput.nativeElement);
    autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
        const place = autocomplete.getPlace();

        if (!this.question.response.customs) {
          this.question.response.customs = {};
        }
        this.question.response.customs.place = place;

        if (place.geometry) {
          this.isValidGoogleMapSelection = true;
          if (place.formatted_address) {
            this.question.response.answer = place.name + '. ' + place.formatted_address;
          } else if (place.name) {
            this.question.response.answer = place.name;
          }
        } else {
          this.isValidGoogleMapSelection = false;
        }
      });
    });
  }

  private initializeQuestion(): void {
    if (!this.question.choices || this.question.choices.length < 2) {
      if (!this.question.choices) this.question.choices = [];
      while (this.question.choices.length < 2) {
        this.addChoice();
      }
    }

    if (this.isResponse && !this.question.response) {
      this.question.response = {
        answer: '',
        choice: null,
        attachments: [],
        status: 'ANSWERED',
        feedback: '',
        customs: { confidence: null }
      };
    }

    this.choiceIdCounter = (this.question.choices?.length || 0) + 1;

    // Inizializza lo stato originale per il tracking delle modifiche (isEdited)
    this.question.originQuestion = null;
    this.question.originQuestion = JSON.parse(JSON.stringify(this.question));

    this.onTypologyChange();
  }

  private disableAllInputs(): void {
    const selector = 'input, textarea, select, button:not(.mat-menu-trigger), mat-checkbox, mat-radio-button, mat-slider, mat-button-toggle-group';
    const elements = Array.from(this.el.nativeElement.querySelectorAll(selector));

    elements.forEach((element: any) => {
      this.renderer.setProperty(element, 'disabled', true);
      this.renderer.addClass(element, 'mat-disabled');
      this.renderer.setAttribute(element, 'disabled', 'true');
    });
  }

  /* --- Gestione Scelte (Option/Checkbox) --- */

  addChoice(): void {
    this.question?.choices?.push({
      id: this.choiceIdCounter++,
      title: '',
      customs: {
        has_open_response: false,
        open_answer_is_mandatory: false,
      },
    });
  }

  removeChoice(id: number): void {
    const index = this.question?.choices.findIndex((c: any) => c.id === id);
    if (index !== -1) {
      this.question.choices.splice(index, 1);
    }
  }

  trackByChoiceId(index: number, item: any): number {
    return item.id;
  }

  /* --- Getters Tipologia --- */

  get isCheckboxQuestion(): boolean { return this.surveyService.isCheckboxQuestion(this.question.typology); }
  get isConsensusQuestion(): boolean { return this.surveyService.isConsensusQuestion(this.question.typology); }
  get isMultipleChoiceQuestion(): boolean { return this.surveyService.isMultipleChoiceQuestion(this.question.typology); }
  get isGridQuestion(): boolean { return this.surveyService.isGridQuestion(this.question.typology); }
  get isQuantitativeQuestion(): boolean { return this.surveyService.isQuantitativeQuestion(this.question.typology); }
  get isOpenedQuestion(): boolean { return this.surveyService.isOpenedQuestion(this.question.typology); }

  get getCustoms(): any {
    if (!this.question.customs) {
      this.question.customs = {};
    }
    return this.question.customs;
  }

  /* --- Azioni --- */

  public duplicateQuestion(): void {
    const tmp = JSON.parse(JSON.stringify(this.question));
    ['id', 'version_number', 'source_id', 'question_history_id'].forEach(key => tmp[key] = null);
    tmp.is_new = true;
    this.questionDuplicated.emit(tmp);
  }

  public removeQuestion(): void {
    this.questionDeleted.emit(this.question.id);
  }

  public onTypologyChange(): void {
    if (!this.isResponse && this.editable) {
      if (this.isConsensusQuestion) {
        this.getCustoms.use_slider = true;
        this.getCustoms.min = 1;
        this.getCustoms.max = 100;
        this.getCustoms.step = 1;
      } else if (!this.isQuantitativeQuestion) {
        this.getCustoms.use_slider = false;
      }


      if (this.isGridQuestion) {
        // Initialize grid_rows if not present or empty
        if (!this.question.grid_rows || this.question.grid_rows.length === 0) {
          this.question.grid_rows = [
            { id: 'row1', title: 'Riga 1', answer_id: null },
            { id: 'row2', title: 'Riga 2', answer_id: null },
          ];
        }
        // Initialize grid_columns if not present or empty
        if (
          !this.question.grid_columns ||
          this.question.grid_columns.length === 0
        ) {
          this.question.grid_columns = [
            { id: 'col1', title: 'Colonna 1' },
            { id: 'col2', title: 'Colonna 2' },
          ];
        }
      }
    }
  }

  /* --- Logica Allegati --- */

  public onClickAddNewEvidence(attachmentType: 'PUBMED' | 'FILE'): void {
    const dialogRef = this.dialog.open(ModalAddAttachmentComponent, {
      panelClass: 'large-dialog',
      data: {
        title: 'modals.modal-add-attachment.add-evidences',
        attachmentType: attachmentType,
        uploadUrl: 'upload/tmp-expert-proposal-file',
      },
    });

    dialogRef.afterClosed().subscribe((attachmentData) => {
      if (!attachmentData) return;

      const targetArray = this.isResponse ? this.question.response.attachments : this.question.attachments;

      if (attachmentData.attachmentType === 'PUBMED') {
        targetArray.push({
          type: 'PUBMED',
          PMID: attachmentData.attachmentObject.PMID,
          title: attachmentData.attachmentObject.ArticleTitle,
          link: attachmentData.attachmentObject.link,
          created_at: new Date().toISOString(),
          isNew: true,
        });
      } else if (attachmentData.attachmentObject) {
        attachmentData.attachmentObject.forEach((file: any) => {
          targetArray.push({
            ...file,
            type: 'FILE',
            created_at: new Date().toISOString(),
            isNew: true,
          });
        });
      }
    });
  }

  public showAttachments(): void {
    this.dialog.open(ModalShowEvidencesComponent, {
      panelClass: 'large-dialog',
      data: { attachments: this.question.attachments },
    });
  }

  /* --- Metodi ripristinati per compatibilità con il Parent Component --- */

  public reset(): void {
    this.question.originQuestion = null;
    this.question.originQuestion = JSON.parse(JSON.stringify(this.question));
    this.question.is_new = false;
  }

  // Helper function per isEdited
  private fromEntries(entries: [string, any][]): Record<string, any> {
    const obj: Record<string, any> = {};
    for (const [key, value] of entries) {
      obj[key] = value;
    }
    return obj;
  }

  public isEdited(): boolean {
    const excludedFields = [
      'id', 'created_at', 'updated_at', 'deleted_at',
      'question_history_id', 'survey_id', 'version_number',
      'responses', 'results', 'originQuestion', 'is_edited_on_revision'
    ];

    const clean = (obj: any): any => {
      if (!obj) return null;
      return this.fromEntries(
        Object.entries(obj)
          .filter(([key]) => !excludedFields.includes(key))
          .map(([key, value]) => {
            if (Array.isArray(value)) {
              return [key, value.map(v => typeof v === 'object' && v !== null ? clean(v) : v)];
            } else if (typeof value === 'object' && value !== null) {
              return [key, clean(value)];
            } else {
              return [key, value];
            }
          })
      );
    };

    const currentClean = clean(this.question);
    const originClean = clean(this.question?.originQuestion);

    const isEdited = JSON.stringify(currentClean) !== JSON.stringify(originClean);
    this.question.is_edited_on_revision = isEdited;

    return isEdited;
  }

  get isRevision(): boolean {
    return this.survey?.version_number && this.survey?.version_number !== "1.0";
  }

  public settingsItemSelected(event) {
    event.stopPropagation();
  }
}
